import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, Camera, ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { travelPhotos, wishLocations } from "@/data/photos";
import type { TravelPhoto, WishLocation } from "@/data/photos";
import UploadModal from "@/components/UploadModal";

/**
 * 自定义照片缩略图标记图标
 * 使用旅行照片本身作为地图标记，带圆角和白色边框
 */
function createPhotoIcon(photo: TravelPhoto) {
  const size = 64;
  const border = 3;
  const shadow = "0 2px 8px oklch(0.20 0.06 45 / 0.35)";
  return L.divIcon({
    className: "custom-photo-marker",
    html: `<div style="
      width:${size}px;height:${size}px;
      border-radius:8px;
      background-image:url(${photo.thumbnail});
      background-size:cover;
      background-position:center;
      border:${border}px solid white;
      box-shadow:${shadow};
      cursor:pointer;
      transition:transform 0.2s ease;
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
    " onmouseover="this.style.transform='scale(1.15)'" onmouseout="this.style.transform='scale(1)'"
    ></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

/**
 * 心愿单问号图标
 */
function createWishIcon() {
  return L.divIcon({
    className: "custom-wish-marker",
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:oklch(0.55 0.10 240 / 0.5);
      border:2px solid oklch(0.55 0.10 240 / 0.7);
      display:flex;align-items:center;justify-content:center;
      color:white;font-weight:bold;font-size:14px;
    ">?</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
}

/**
 * 旅行地图页面
 * 使用 Leaflet + MarkerCluster 实现聚合/分散效果
 * 支持上传带 GPS 的照片动态添加到地图
 */
export default function MapPage() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markerClusterRef = useRef<any>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<TravelPhoto | null>(null);
  const [customPhotos, setCustomPhotos] = useState<TravelPhoto[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  // 追踪已添加到地图的照片 ID，避免重复
  const addedPhotoIdsRef = useRef<Set<number>>(new Set());

  const allPhotos = [...travelPhotos, ...customPhotos];

  // 直接从照片数据中的 city 字段统计城市
  const cityStats = useMemo(() => {
    const map = new Map<string, number>();
    allPhotos.forEach((p) => {
      if (p.city) {
        map.set(p.city, (map.get(p.city) || 0) + 1);
      }
    });
    return map;
  }, [allPhotos]);

  const citiesVisited = cityStats.size;

  // 计算时间线统计：按年份分组
  const timelineStats = useMemo(() => {
    const yearMap = new Map<number, number>();
    allPhotos.forEach((p) => {
      const year = p.year || new Date(p.date).getFullYear() || 0;
      if (year > 0) {
        yearMap.set(year, (yearMap.get(year) || 0) + 1);
      }
    });
    return Array.from(yearMap.entries()).sort((a, b) => b[0] - a[0]);
  }, [allPhotos]);

  const yearsActive = timelineStats.length;

  // 当 customPhotos 变化时，添加新标记到地图
  useEffect(() => {
    if (markerClusterRef.current && customPhotos.length > 0) {
      customPhotos.forEach((photo) => {
        if (!addedPhotoIdsRef.current.has(photo.id)) {
          const marker = L.marker([photo.lat, photo.lng], { icon: createPhotoIcon(photo) });
          marker.on("click", () => setSelectedPhoto(photo));
          markerClusterRef.current.addLayer(marker);
          addedPhotoIdsRef.current.add(photo.id);
        }
      });
    }
  }, [customPhotos]);

  // 添加单个照片标记到聚合组（保留供未来使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _addPhotoMarker = useCallback((photo: TravelPhoto) => {
    if (!markerClusterRef.current) {
      toast.error("地图标记层未初始化，请刷新页面重试");
      return;
    }
    if (typeof photo.lat !== "number" || typeof photo.lng !== "number" || isNaN(photo.lat) || isNaN(photo.lng)) {
      toast.error(`坐标无效: lat=${photo.lat}, lng=${photo.lng}`);
      return;
    }
    const marker = L.marker([photo.lat, photo.lng], { icon: createPhotoIcon(photo) });
    marker.on("click", () => setSelectedPhoto(photo));
    markerClusterRef.current.addLayer(marker);
  }, []);

  // 初始化地图（仅执行一次）
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView([35.0, 105.0], 4);

    mapInstance.current = map;

    L.control.zoom({ position: "bottomright" }).addTo(map);

    L.tileLayer("https://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=7&x={x}&y={y}&z={z}", {
      attribution: "&copy; 高德地图",
      subdomains: "1234",
      maxZoom: 18,
      minZoom: 3,
    }).addTo(map);

    const markerCluster = (L as any).markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 60,
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount();
        const markers = cluster.getAllChildMarkers();
        const firstPhoto = markers[0]?.options?.icon?.options?.html
          ? markers[0].options.icon.options.html.match(/background-image:url\(([^)]+)\)/)?.[1]
          : null;
        const size = 64;
        const badgeSize = 22;

        const photoHtml = firstPhoto
          ? `<div style="
              width:${size}px;height:${size}px;
              border-radius:8px;
              background-image:url(${firstPhoto});
              background-size:cover;
              background-position:center;
              border:3px solid white;
              box-shadow:0 2px 8px oklch(0.20 0.06 45 / 0.35);
              position:relative;
              cursor:pointer;
              transition:transform 0.2s ease;
            " onmouseover="this.style.transform='scale(1.15)'" onmouseout="this.style.transform='scale(1)'"
            >
              <div style="
                position:absolute;
                top:-6px;
                right:-6px;
                min-width:${badgeSize}px;
                height:${badgeSize}px;
                padding:0 6px;
                border-radius:11px;
                background:oklch(0.62 0.18 35);
                color:white;
                display:flex;
                align-items:center;
                justify-content:center;
                font-weight:700;
                font-size:12px;
                box-shadow:0 1px 4px oklch(0.45 0.10 35 / 0.4);
                border:2px solid white;
                box-sizing:border-box;
                z-index:1;
              ">${count}</div>
            </div>`
          : `<div style="
              width:${size}px;height:${size}px;
              border-radius:8px;
              background:oklch(0.62 0.18 35 / 0.85);
              color:white;
              display:flex;align-items:center;justify-content:center;
              font-weight:700;font-size:14px;
              box-shadow:0 2px 8px oklch(0.45 0.10 35 / 0.3);
              border:3px solid white;
              position:relative;
              cursor:pointer;
            ">
              ${count}
            </div>`;

        return L.divIcon({
          className: "custom-cluster",
          html: photoHtml,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      },
    });

    markerClusterRef.current = markerCluster;

    // 添加初始照片标记
    travelPhotos.forEach((photo) => {
      const marker = L.marker([photo.lat, photo.lng], { icon: createPhotoIcon(photo) });
      marker.on("click", () => setSelectedPhoto(photo));
      markerCluster.addLayer(marker);
      addedPhotoIdsRef.current.add(photo.id);
    });

    map.addLayer(markerCluster);

    // 添加心愿单标记
    wishLocations.forEach((wish: WishLocation) => {
      const marker = L.marker([wish.lat, wish.lng], { icon: createWishIcon() });
      marker.bindPopup(
        `<div style="padding:8px;text-align:center;">
          <div style="font-weight:600;color:oklch(0.40 0.06 45);margin-bottom:4px;">${wish.name}</div>
          <div style="font-size:12px;color:oklch(0.50 0.04 55);">${wish.description}</div>
        </div>`,
        { closeButton: false, offset: [0, -10] }
      );
      marker.addTo(map);
    });

    // 自动适配所有标记点
    const photoPoints = travelPhotos.map((p) => [p.lat, p.lng] as [number, number]);
    if (photoPoints.length > 0) {
      map.fitBounds(L.latLngBounds(photoPoints), { padding: [80, 80], maxZoom: 6 });
    }

    // 加载 localStorage 中保存的自定义照片
    try {
      const saved = localStorage.getItem("customPhotos");
      if (saved) {
        const parsed: TravelPhoto[] = JSON.parse(saved);
        if (parsed.length > 0) {
          setCustomPhotos(parsed);
          parsed.forEach((photo) => {
            const marker = L.marker([photo.lat, photo.lng], { icon: createPhotoIcon(photo) });
            marker.on("click", () => setSelectedPhoto(photo));
            markerCluster.addLayer(marker);
            addedPhotoIdsRef.current.add(photo.id);
          });
        }
      }
    } catch (err) {
      console.error("加载自定义照片失败:", err);
    }

    return () => {
      map.remove();
      mapInstance.current = null;
      markerClusterRef.current = null;
    };
  }, []);

  // 处理上传解析完成的照片
  const handlePhotosParsed = useCallback((photos: TravelPhoto[]) => {
    try {
      toast.info(`正在添加 ${photos.length} 张照片到地图...`);

      // 更新状态并保存到 localStorage
      setCustomPhotos((prev) => {
        const newCustomPhotos = [...prev, ...photos];
        localStorage.setItem("customPhotos", JSON.stringify(newCustomPhotos));
        return newCustomPhotos;
      });

      // 自动缩放到包含新标记的区域
      if (mapInstance.current && photos.length > 0) {
        const newPoints = photos.map((p) => [p.lat, p.lng] as [number, number]);
        const bounds = L.latLngBounds(newPoints);
        mapInstance.current.fitBounds(bounds, { padding: [100, 100], maxZoom: 12 });
      }

      toast.success(`已成功添加 ${photos.length} 张照片到地图`);
    } catch (err) {
      toast.error("添加照片到地图失败: " + (err as Error).message);
      console.error("handlePhotosParsed error:", err);
    }
  }, []);

  // 城市列表排序
  const sortedCities = useMemo(() => {
    return Array.from(cityStats.entries())
      .sort((a, b) => b[1] - a[1]);
  }, [cityStats]);

  return (
    <div className="min-h-screen flex flex-col" style={{ paddingTop: 64 }}>
      {/* Header */}
      <div
        className="flex items-center justify-between sticky z-30"
        style={{
          top: 64,
          padding: "var(--spacing-md) var(--spacing-lg)",
          backgroundColor: "oklch(1 0 0 / 0.95)",
          backdropFilter: "blur(16px) saturate(1.2)",
          WebkitBackdropFilter: "blur(16px) saturate(1.2)",
          boxShadow: "0 1px 3px oklch(0.55 0.08 55 / 0.06)",
          borderBottom: "1px solid oklch(0.90 0.025 65 / 0.4)",
        }}
      >
        <div className="flex items-center" style={{ gap: "var(--spacing-md)" }}>
          <Link
            to="/"
            className="flex items-center rounded-xl transition-all duration-200 hover:bg-muted"
            style={{ gap: "var(--spacing-xs)", padding: "var(--spacing-xs)" }}
          >
            <ArrowLeft className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
          </Link>
          <div>
            <h1
              style={{
                fontFamily: "var(--font-handwritten)",
                fontSize: "var(--font-size-title)",
                color: "var(--foreground)",
              }}
            >
              旅行地图
            </h1>
            <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
              共 {allPhotos.length} 张照片 · 点击标记查看详情
            </p>
          </div>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            toast("打开上传窗口...");
            setUploadOpen(true);
          }}
          className="flex items-center rounded-xl transition-shadow duration-300"
          style={{
            gap: "var(--spacing-sm)",
            padding: "var(--spacing-sm) var(--spacing-md)",
            background: "linear-gradient(135deg, oklch(0.62 0.18 35) 0%, oklch(0.58 0.2 32) 100%)",
            color: "white",
            fontSize: "var(--font-size-label)",
            fontWeight: 500,
            boxShadow: "0 4px 12px oklch(0.55 0.15 35 / 0.25)",
          }}
        >
          <Plus className="w-4 h-4" />
          上传照片
        </motion.button>
      </div>

      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Sidebar stats */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:w-72 flex-shrink-0 overflow-y-auto"
          style={{
            padding: "var(--spacing-lg)",
            backgroundColor: "oklch(0.97 0.015 70 / 0.7)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            borderRight: "1px solid oklch(0.90 0.025 65 / 0.4)",
            maxHeight: "calc(100vh - 120px)",
          }}
        >
          <div className="flex flex-col" style={{ gap: "var(--spacing-md)" }}>
            <h3
              style={{
                fontFamily: "var(--font-handwritten)",
                fontSize: "var(--font-size-title)",
                color: "var(--foreground)",
                marginBottom: "var(--spacing-xs)",
              }}
            >
              旅行统计
            </h3>

            {/* 照片总数 */}
            {[
              { label: "照片总数", value: `${allPhotos.length} 张`, icon: Camera, color: "oklch(0.65 0.14 160)", bg: "oklch(0.65 0.14 160 / 0.08)" },
            ].map((stat) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  whileHover={{ y: -2 }}
                  className="flex items-center rounded-xl transition-shadow duration-300"
                  style={{
                    gap: "var(--spacing-sm)",
                    padding: "var(--spacing-md)",
                    backgroundColor: "oklch(1 0 0 / 0.9)",
                    boxShadow: "var(--ds-shadow-sm)",
                    border: "1px solid oklch(0.92 0.02 65 / 0.4)",
                    cursor: "default",
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded-xl"
                    style={{
                      width: 44,
                      height: 44,
                      backgroundColor: stat.bg,
                    }}
                  >
                    <Icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                      {stat.label}
                    </p>
                    <p className="font-semibold" style={{ fontSize: "var(--font-size-body)", color: "var(--foreground)" }}>
                      {stat.value}
                    </p>
                  </div>
                </motion.div>
              );
            })}

            {/* 解锁城市 - 直接从照片数据中的 city 字段统计 */}
            <div
              className="rounded-xl overflow-hidden transition-shadow duration-300"
              style={{
                backgroundColor: "oklch(1 0 0 / 0.9)",
                boxShadow: "var(--ds-shadow-sm)",
                border: "1px solid oklch(0.92 0.02 65 / 0.4)",
              }}
            >
              {/* 城市统计头部 */}
              <div className="flex items-center" style={{ gap: "var(--spacing-sm)", padding: "var(--spacing-md)", borderBottom: "1px solid oklch(0.90 0.025 65 / 0.4)" }}>
                <div
                  className="flex items-center justify-center rounded-xl"
                  style={{ width: 44, height: 44, backgroundColor: "oklch(0.62 0.18 35 / 0.08)" }}
                >
                  <MapPin className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
                </div>
                <div>
                  <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>解锁城市</p>
                  <p className="font-semibold" style={{ fontSize: "var(--font-size-body)", color: "var(--foreground)" }}>
                    {citiesVisited} 个
                  </p>
                </div>
              </div>

              {/* 城市列表 */}
              {sortedCities.length > 0 ? (
                <div className="flex flex-col" style={{ gap: "var(--spacing-xs)", padding: "var(--spacing-sm) var(--spacing-md) var(--spacing-md)" }}>
                  {sortedCities.slice(0, 10).map(([cityName, count]) => (
                    <div key={cityName} className="flex items-center justify-between rounded-lg" style={{ padding: "var(--spacing-xs) var(--spacing-sm)" }}>
                      <span style={{ fontSize: "var(--font-size-label)", color: "var(--foreground)" }}>
                        {cityName}
                      </span>
                      <span style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                        {count} 张
                      </span>
                    </div>
                  ))}
                  {sortedCities.length > 10 && (
                    <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)", textAlign: "center", paddingTop: "var(--spacing-xs)" }}>
                      ... 还有 {sortedCities.length - 10} 个城市
                    </p>
                  )}
                </div>
              ) : (
                <div style={{ padding: "var(--spacing-md)", textAlign: "center" }}>
                  <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                    暂无城市数据
                  </p>
                </div>
              )}
            </div>

            {/* 旅行时间线 - 按年份统计照片分布 */}
            <div
              className="rounded-xl overflow-hidden"
              style={{
                backgroundColor: "oklch(1 0 0 / 0.9)",
                boxShadow: "var(--ds-shadow-sm)",
                border: "1px solid oklch(0.92 0.02 65 / 0.4)",
              }}
            >
              {/* 时间线头部 */}
              <div className="flex items-center" style={{ gap: "var(--spacing-sm)", padding: "var(--spacing-md)", borderBottom: "1px solid oklch(0.90 0.025 65 / 0.4)" }}>
                <div
                  className="flex items-center justify-center rounded-xl"
                  style={{ width: 44, height: 44, backgroundColor: "oklch(0.75 0.16 55 / 0.08)" }}
                >
                  <Calendar className="w-5 h-5" style={{ color: "oklch(0.75 0.16 55)" }} />
                </div>
                <div>
                  <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>旅行时间线</p>
                  <p className="font-semibold" style={{ fontSize: "var(--font-size-body)", color: "var(--foreground)" }}>
                    {yearsActive} 个年份
                  </p>
                </div>
              </div>

              {/* 年份列表 */}
              {timelineStats.length > 0 ? (
                <div className="flex flex-col" style={{ gap: "var(--spacing-xs)", padding: "var(--spacing-sm) var(--spacing-md) var(--spacing-md)" }}>
                  {timelineStats.map(([year, count]) => (
                    <div key={year} className="flex items-center justify-between rounded-lg" style={{ padding: "var(--spacing-xs) var(--spacing-sm)" }}>
                      <span style={{ fontSize: "var(--font-size-label)", color: "var(--foreground)" }}>
                        {year} 年
                      </span>
                      <span style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                        {count} 张
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: "var(--spacing-md)", textAlign: "center" }}>
                  <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                    暂无时间数据
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Map */}
        <div className="flex-1 relative" style={{ minHeight: 500 }}>
          <div ref={mapRef} className="absolute inset-0" />

          {/* Photo detail modal */}
          <AnimatePresence>
            {selectedPhoto && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[500] flex items-center justify-center p-4"
                style={{
                  backgroundColor: "oklch(0.18 0.06 45 / 0.55)",
                }}
                onClick={() => setSelectedPhoto(null)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  className="w-full max-w-sm rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: "oklch(1 0 0)",
                    boxShadow: "var(--ds-shadow-xl)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={selectedPhoto.thumbnail}
                    alt={selectedPhoto.title}
                    className="w-full h-56 object-cover"
                  />
                  <div style={{ padding: "var(--spacing-md)" }}>
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold" style={{ fontSize: "var(--font-size-body)", color: "var(--foreground)" }}>
                        {selectedPhoto.title}
                      </h4>
                      <span style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                        {selectedPhoto.date}
                      </span>
                    </div>
                    <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)", marginTop: "var(--spacing-xs)" }}>
                      {selectedPhoto.city} · {selectedPhoto.country}
                    </p>
                    <p
                      className="mt-2"
                      style={{
                        fontFamily: "var(--font-handwritten)",
                        fontSize: "var(--font-size-body)",
                        color: "var(--foreground)",
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedPhoto.mood}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onPhotosParsed={handlePhotosParsed}
      />
    </div>
  );
}
